import { useAppSelector } from 'flow/hooks/useRedux';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import ContactTypeIcon from 'flow/components/contact/ContactTypeIcon';
import ContactContextMenu from 'flow/components/context-menus/ContactContextMenu';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import {
    extractName,
    extractNumberOrEmail,
    extractStatusFromProfile,
    isContactKey,
    isExtensionKey,
    isThirdPartyContactKey,
} from 'flow/utils';
import { setRecentColleague } from 'flow/utils/browser-utils';
import {
    getFixedNumberFromContact,
    getFixedOrTechnicalOrMobileNumberFromContact,
    getMobileNumberFromContact,
    getPhoneNumberFromContact,
} from 'flow/utils/extension-utils';
import { getParsedFuzzyTexts } from 'flow/utils/search-utils';
import { twMerge } from 'tailwind-merge';
import CallAdaptiveButton from '../CallAdaptiveButton';
import ContextMenu from '../ContextMenu';
import BaseListItem, { rowMarking } from './BaseListItem';
import ContactAvatar from './ContactAvatar';
import AiContactContextMenu from '../context-menus/AiContactContextMenu';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { selectExtensionActiveProfile } from 'flow/selectors/ExtensionSelectors';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    contact: TypedMap<Contact>;
    contactType: string;
    fuzzyMatches?: any;
    extensionKey?: ExtensionKey;
}

const ListItemContact = ({
    contact,
    contactType,
    fuzzyMatches,
    extensionKey,
}: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();

    const [itemIsActive, setItemIsActive] = useState(false);

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey),
    );

    const entityKey = extensionKey || contact?.get('key');

    const { status, extension } = useExtension(extensionKey);
    const isAiCoworker = extension?.get('extensionType') === 'ai_coworker';

    const name = useMemo(
        () => extractName(contact, i18next.t('status.unknown')),
        [contact, i18next],
    );
    const socialIntegrationContactsNumber = useMemo(
        () => getPhoneNumberFromContact(contact),
        [contact],
    );
    const fixedNumber = useMemo(
        () => getFixedNumberFromContact(contact),
        [contact],
    );
    const mobileNumber = useMemo(
        () => getMobileNumberFromContact(contact),
        [contact],
    );

    let number = useMemo(
        () => getFixedOrTechnicalOrMobileNumberFromContact(contact),
        [contact],
    );

    if (contactType === 'socialIntegrationContacts') {
        number = socialIntegrationContactsNumber;
    } else if (contactType === 'externalContacts') {
        number = fixedNumber || mobileNumber;
    }

    const [fuzzyParsedName, fuzzyParsedOther] = getParsedFuzzyTexts(
        fuzzyMatches,
        i18next,
        'search.contact',
    );

    const initialItemDescription =
        fuzzyParsedOther ||
        (contactType === 'socialIntegrationContacts'
            ? extractNumberOrEmail(contact)
            : extractStatusFromProfile(activeProfile, i18next));

    const itemFlowStatus =
        typeof initialItemDescription === 'string' &&
        initialItemDescription.trim() === ''
            ? null
            : initialItemDescription;

    const handleNavigate = () => {
        if (isExtensionKey(entityKey)) {
            history.push(
                generatePath(routes['extensions.entityKey.contact'], {
                    entityKey,
                }),
            );
            setRecentColleague(entityKey);
        } else if (isContactKey(entityKey)) {
            history.push(
                generatePath(routes['extensions.contact.contactKey'], {
                    contactKey: entityKey,
                }),
            );
        } else if (isThirdPartyContactKey(entityKey)) {
            history.push(
                generatePath(
                    routes['extensions.thirdpartycontact.thirdPartyContactKey'],
                    { thirdPartyContactKey: entityKey },
                ),
            );
        }
    };

    return (
        <BaseListItem
            onClick={handleNavigate}
            marking={rowMarking({ highlighted: itemIsActive })}
        >
            <ContactAvatar contact={contact} status={status} />
            <div
                className="flex w-full flex-col overflow-hidden whitespace-nowrap"
                id={number}
            >
                <div className="m-auto flex w-full flex-row justify-between">
                    <div className="overflow-hidden leading-5 text-ellipsis">
                        {fuzzyParsedName || name}
                    </div>
                    <ContactTypeIcon contact={contact} />
                </div>
                {itemFlowStatus ? (
                    <div className="text-sm/4 text-gray-500">
                        {itemFlowStatus}
                    </div>
                ) : null}
            </div>
            <div
                className={twMerge(
                    'items-center',
                    itemIsActive ? 'flex' : 'sr-only-until-hover-or-focus flex',
                )}
            >
                <ContextMenu
                    triggerLabel={getMoreOptionsLabel(i18next, name)}
                    setActive={setItemIsActive}
                    lockScroll={{
                        scroll: document.querySelector(
                            '#central-switch-overview',
                        ),
                        wrapper: document.querySelector(
                            '#central-switch-overview',
                        ),
                    }}
                >
                    {isAiCoworker ? (
                        <AiContactContextMenu extensionKey={entityKey} />
                    ) : (
                        <ContactContextMenu entityKey={entityKey} />
                    )}
                </ContextMenu>
            </div>
            {number ? (
                <div
                    className={twMerge(
                        'items-center',
                        itemIsActive
                            ? 'flex'
                            : 'sr-only-until-hover-or-focus flex',
                    )}
                >
                    <CallAdaptiveButton
                        phoneNumber={number}
                        extensionKey={extensionKey}
                        activeBg={itemIsActive}
                    />
                </div>
            ) : null}
        </BaseListItem>
    );
};

export default ListItemContact;
